import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import { GROUPED_STATIC_TEXT_ROLE } from '../../../components/ui-components/NameValueTableRow'
import type { TaxHarvestingSummaryResponse } from '../types/summary.types'
import { GainType } from '../types/summary.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { NoTaxSavings } from '../components/NoTaxSavings'
import {
  TaxHarvestingScreenName,
  TaxHarvestingBottomSheetName,
  TaxHarvestingEventId,
  trackTaxHarvestingBottomSheetLoaded,
  trackTaxHarvestingUserClicked
} from '../tracking/taxHarvestingTracking'
import { updateTaxBracket } from '../api/updateTaxBracket'

type ViewType = 'summary' | 'tax'

type Props = {
  onDismiss: () => void
  view: ViewType
  summary?: TaxHarvestingSummaryResponse
  shortTermGainsSummary?: TaxHarvestingSummaryResponse['shortTermGainsSummary']
}

export default function StcgBottomSheet({ onDismiss, view, summary, shortTermGainsSummary }: Props) {
  const isSummary = view === 'summary'
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(
      isSummary ? TaxHarvestingBottomSheetName.StcgSummary : TaxHarvestingBottomSheetName.StcgTaxLiability
    )
  }, [isSummary])

  return (
    <BottomSheetContent
      heading={isSummary ? 'STCG Summary' : 'Tax Liability (STCG)'}
      bottomSheetHeadingClassName={isSummary ? '!mb-0' : ''}
      onDismiss={onDismiss}
    >
      {isSummary && <p className='mb-4 text-sm text-grey40'>Gains from short-term investments sold this FY</p>}
      {isSummary ? <StcgSummary summary={summary} shortTermGainsSummary={summary?.shortTermGainsSummary} /> : <StcgTaxLiability summary={summary} shortTermGainsSummary={shortTermGainsSummary} />}
    </BottomSheetContent>
  )
}

const SectionRow = ({
  label,
  value,
  helper,
  highlight
}: {
  label: string
  value: string
  helper?: string
  highlight?: boolean
}) => (
  <div
    role={GROUPED_STATIC_TEXT_ROLE}
    className='flex items-start justify-between text-sm py-2'
    aria-label={`${label}${helper ? `, ${helper}` : ''}, ${value}`}
  >
    <div className='flex flex-col gap-[2px]' aria-hidden='true'>
      <span className='text-[#6A6E6E]'>{label}</span>
      {helper && <span className='text-sm text-[#8E9393]'>{helper}</span>}
    </div>
    <span aria-hidden='true' className={`font-medium ${highlight ? 'text-[#C2351C]' : 'text-[#232323]'}`}>{value}</span>
  </div>
)

type StcgSummaryProps = {
  summary?: TaxHarvestingSummaryResponse
  shortTermGainsSummary?: TaxHarvestingSummaryResponse['shortTermGainsSummary']
}

const StcgSummary = ({ summary, shortTermGainsSummary }: StcgSummaryProps) => {
  const equityStcg = summary?.equityStcg ?? 0
  const nonEquityStcg = summary?.nonEquityStcg ?? 0
  const equityStcl = summary?.equityStcl ?? 0
  const nonEquityStcl = summary?.nonEquityStcl ?? 0
  const totalStcl = Math.abs(equityStcl + nonEquityStcl)
  const netEquityStcg = equityStcg - equityStcl
  const netNonEquityStcg = nonEquityStcg - nonEquityStcl
  const netRealisedStcg = netEquityStcg + netNonEquityStcg

  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`

  return (
    <div className='text-[14px] text-[#6A6E6E] leading-[150%] pb-4 flex flex-col gap-4'>
      <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2 bg-white'>
        <SectionRow label='Equity STCG' value={format(equityStcg)} />
        <SectionRow label='Non-equity STCG' value={format(nonEquityStcg)} />
      </div>
      <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2 bg-white'>
        <SectionRow
          label='Equity + Non-equity STCL'
          value={`${totalStcl !== 0 ? '-' : ''} ${format(totalStcl)}`}
          highlight
        />
      </div>
      <div className='bg-[#E6F2FF] rounded-xl'>
        <div
          role={GROUPED_STATIC_TEXT_ROLE}
          className='px-4 py-4 flex items-center justify-between border-b border-[#004D991A]'
          aria-label={`Net realised STCG, ${format(netRealisedStcg)}`}
        >
          <span aria-hidden='true' className='font-medium text-[#232323]'>Net realised STCG</span>
          <span aria-hidden='true' className='font-medium text-[#232323]'>{format(netRealisedStcg)}</span>
        </div>
        <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2'>
          <SectionRow label='Net Equity STCG' value={`${format((shortTermGainsSummary?.equityStcg ?? 0) - ((shortTermGainsSummary?.equityStcl ?? 0) + (shortTermGainsSummary?.nonEquityStcl ?? 0)) + (shortTermGainsSummary?.stclOffsettingNeqStcg ?? 0))}`} />
          <SectionRow label='Net Non-equity STCG' value={`${format((shortTermGainsSummary?.nonEquityStcg ?? 0) - (shortTermGainsSummary?.stclOffsettingNeqStcg ?? 0))}`} />
        </div>
      </div>
    </div>
  )
}

type StcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  shortTermGainsSummary?: TaxHarvestingSummaryResponse['shortTermGainsSummary']
}

const StcgTaxLiability = ({ summary, shortTermGainsSummary }: StcgTaxLiabilityProps) => {
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`
  const [totalTaxLiability, setTotalTaxLiability] = useState(summary?.taxLiabilityStcg ?? 0)

  return (
    <div className='text-[14px] text-[#6A6E6E] leading-[150%] pb-4 flex flex-col gap-4'>
      {/* Plan highlights (including NoTaxSavings) are only shown on the Plan page, not inside this generic STCG bottom sheet */}
      <EquityStcgTaxLiability summary={summary} shortTermGainsSummary={shortTermGainsSummary} showPlanHighlights={false} />
      <NonEquityStcgTaxLiability
        summary={summary}
        shortTermGainsSummary={shortTermGainsSummary}
        showPlanHighlights={false}
        onSlabTaxChange={(newSlabTax) => {
          const equityTax = summary?.taxLiabilityEquityStcg ?? 0
          setTotalTaxLiability(equityTax + newSlabTax)
        }}
      />
      <div
        role={GROUPED_STATIC_TEXT_ROLE}
        className='mt-3 rounded-xl bg-[#E6F1FF] px-4 py-4 flex items-center justify-between'
        aria-label={`Total tax liability, ${format(totalTaxLiability)}`}
      >
        <span aria-hidden='true' className='text-[#232323] font-medium'>Total tax liability</span>
        <span aria-hidden='true' className='font-medium text-[#232323]'>{format(totalTaxLiability)}</span>
      </div>
    </div>
  )
}

type EquityStcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  /** When true, show plan-specific green info box (used only on plan page) */
  showPlanHighlights?: boolean
  /** Possible tax saving from the equity STCG plan, if available */
  equityPlanPossibleTaxSave?: number
  shortTermGainsSummary?: TaxHarvestingSummaryResponse['shortTermGainsSummary']
}

export const EquityStcgTaxLiability = ({
  summary,
  showPlanHighlights,
  equityPlanPossibleTaxSave,
  shortTermGainsSummary

}: EquityStcgTaxLiabilityProps) => {
  console.log(shortTermGainsSummary)
  const equityStcgRate = summary?.equityStcgRate ?? 20
  const equityStcg = summary?.equityStcg ?? 0
  const equityStcl = summary?.equityStcl ?? 0
  const netEquityStcg = equityStcg - equityStcl
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`
  const hasTaxSavingOpportunity = (equityPlanPossibleTaxSave ?? 0) > 0
  const taxLiabilityEquityStcg = summary?.taxLiabilityEquityStcg ?? 0

  return (
    <div className='flex flex-col gap-3'>
      <div className='border border-[#EBF2F2] rounded-[16px] pt-3 pb-[1px] px-1 bg-white'>
        <div className='px-3 mb-4'>
          <SectionRow label='Net equity STCG' value={`${format((shortTermGainsSummary?.equityStcg ?? 0) - ((shortTermGainsSummary?.equityStcl ?? 0) + (shortTermGainsSummary?.nonEquityStcl ?? 0)) + (shortTermGainsSummary?.stclOffsettingNeqStcg ?? 0))}`} />
          <SectionRow label={`Tax @ ${equityStcgRate}%`} value={format(taxLiabilityEquityStcg)} />
        </div>
        {showPlanHighlights && hasTaxSavingOpportunity && (
          <div className='rounded-[12px] bg-[#FFF2DF] px-4 py-3'>
            <div className='text-sm font-medium text-orange10'>
              Save <span className='font-semibold'>{format(equityPlanPossibleTaxSave ?? 0)}</span> in tax
            </div>
            <div className='mt-1 text-13 text-orange10 opacity-60'>
              You can offset short-term gains by booking losses in eligible funds.
            </div>
          </div>
        )}

        {showPlanHighlights && !hasTaxSavingOpportunity && (
          <div className='mt-3'>
            <NoTaxSavings
              gainType={GainType.STCG}
              taxLiability={format(taxLiabilityEquityStcg)}
              showChevron={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}

type NonEquityStcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  showPlanHighlights?: boolean
  shortTermGainsSummary?: TaxHarvestingSummaryResponse['shortTermGainsSummary']
  onSlabTaxChange?: (slabTax: number) => void
}

export const NonEquityStcgTaxLiability = ({
  summary,
  showPlanHighlights,
  shortTermGainsSummary,
  onSlabTaxChange
}: NonEquityStcgTaxLiabilityProps) => {
  const nonEquityStcgRate = summary?.nonEquityStcgRate ?? 20
  const netNonEquityStcg = (summary?.nonEquityStcg ?? 0) - (summary?.nonEquityStcl ?? 0)
  const [incomeSlabRate, setIncomeSlabRate] = useState(nonEquityStcgRate)
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`

  const stgsNetNonEquityStcg = (shortTermGainsSummary?.nonEquityStcg ?? 0) - (shortTermGainsSummary?.stclOffsettingNeqStcg ?? 0)
  const slabTax = (stgsNetNonEquityStcg * incomeSlabRate) / 100

  useEffect(() => {
    if (onSlabTaxChange) {
      onSlabTaxChange(slabTax)
    }
  }, [slabTax, onSlabTaxChange])

  return (
    <div className='border border-[#EBF2F2] rounded-2xl pt-3 pb-[1px] px-1 bg-white'>
      <div className='px-3 mb-4'>
        <SectionRow label='Net Non-equity STCG' value={`${format(stgsNetNonEquityStcg)}`} />
        <div className='flex items-start justify-between text-sm py-2'>
          <div className='flex flex-col gap-[2px]'>
            <span className='text-[#6A6E6E]'>
              Tax as per income slab {incomeSlabRate}%.
              <span
                className='ml-1 underline cursor-pointer'
                onClick={() => {
                  trackTaxHarvestingUserClicked({
                    screen: TaxHarvestingScreenName.StcgTaxLiability,
                    eventId: TaxHarvestingEventId.IncomeSlabEdit
                  })
                  openNestedBottomSheet(
                    <IncomeSlabSelectionSheet
                      selectedRate={incomeSlabRate}
                      onSelect={(rate) => {
                        setIncomeSlabRate(rate)
                        closeNestedBottomSheet()
                      }}
                      onDismiss={() => {
                        closeNestedBottomSheet()
                      }}
                    />,
                    () => {
                      closeNestedBottomSheet()
                    }
                  )
                }}
              >
                Edit
              </span>
            </span>
          </div>
          <span className='font-medium text-[#232323]'>{format(slabTax)}</span>
        </div>
      </div>

      {showPlanHighlights && (
        netNonEquityStcg === 0 ? (
          <NoTaxSavings
            gainType={GainType.STCG}
            taxLiability={format(slabTax)}
            showChevron={false}
          />
        ) : (
          <div className='mt-1 rounded-2xl bg-[#E6F2FF] px-4 py-3'>
            <div className='text-sm font-medium text-[#004D99]'>
              No tax saving plan available
            </div>
            <div className='mt-1 text-13 text-[#004D99] opacity-60 leading-[1.5]'>
              Non-equity STCG is taxed as per your income tax slab. We aren’t suggesting a plan here since we don’t have complete information of your income.
            </div>
          </div>
        )
      )}
    </div>
  )
}

type IncomeSlabSelectionSheetProps = {
  selectedRate: number
  onSelect: (rate: number) => void
  onDismiss: () => void
}

const INCOME_SLAB_OPTIONS: number[] = [0, 5, 10, 15, 20, 25, 30]

const IncomeSlabSelectionSheet = ({ selectedRate, onSelect, onDismiss }: IncomeSlabSelectionSheetProps) => {
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.IncomeSlabSelection)
  }, [])

  const queryClient = useQueryClient()

  const { mutate: mutateTaxBracket } = useMutation({
    mutationFn: updateTaxBracket,
    onSettled: () => {
      console.log('onSettled')
      queryClient.invalidateQueries({ queryKey: ['taxHarvestingSummary'] })
    }
  })

  return (
    <BottomSheetContent heading='Select your income tax slab' onDismiss={onDismiss} bottomSheetBodyClassName='!px-0'>
      <div className='pt-2 pb-4'>
        {INCOME_SLAB_OPTIONS.map((rate, index) => {
          const isSelected = rate === selectedRate
          return (
            <div key={rate}>
              <div
                className='flex items-center justify-between px-5 py-6 cursor-pointer'
                onClick={() => {
                  trackTaxHarvestingUserClicked({
                    screen: TaxHarvestingScreenName.IncomeSlabSelection,
                    eventId: TaxHarvestingEventId.SlabOption,
                    buttonLabel: rate === 0 ? 'Nil' : `${rate}%`
                  })
                  onSelect(rate)
                  mutateTaxBracket({ taxBracket: rate })
                }}
              >
                <span
                  className={`text-[14px] ${isSelected ? 'font-medium text-[#232323]' : 'font-normal text-[#6A6E6E]'
                    }`}
                >
                  {rate === 0 ? 'Nil' : `${rate}%`}
                </span>
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full border ${isSelected ? 'border-[#232323] bg-[#232323]' : 'border-[#D0D5DD]'
                    }`}
                >
                  {isSelected && <span className='w-2 h-2 rounded-full bg-white' />}
                </span>
              </div>
              {index !== INCOME_SLAB_OPTIONS.length - 1 && <div className='h-px bg-[#F0F4F7]' />}
            </div>
          )
        })}
      </div>
    </BottomSheetContent>
  )
}

export function RealisedStclInfoSheet({ onDismiss }: { onDismiss: () => void }) {
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.RealisedStcl)
  }, [])
  return (
    <BottomSheetContent heading='Remaining STCL' onDismiss={onDismiss}>
      <div className='flex flex-col gap-4 text-[14px] text-[#6A6E6E] leading-[1.5]'>
        <p>
          STCL is the loss from any short-term investments you&apos;ve sold this year. STCL is used to offset all short-term gains first.
        </p>
        <p>
          This is the remaining STCL left, which is being used to offset long-term gains to reduce tax even further.
        </p>
      </div>
    </BottomSheetContent>
  )
}

